import type {OrdinaryProgramOwner} from '../gaming/remote/ordinary-program';
import type {OrdinaryProgramFactory} from '../gaming/remote/ordinary-provider';
import {createReactionSessionPublisher} from '../gaming/reaction-session-publisher';
import {fenceCreatorQualification,type CreatorQualifier,type CreatorQualificationContext} from '../gaming/creator-qualification';
import {CreatorProgram} from '../gaming/creator-program';
import {CreatorSource} from '../gaming/creator-source';
import {CreatorSourceOwner} from '../gaming/creator-source-owner';
import { ReactionController } from '../gaming/reaction-controller';
import { ReactionTap } from '../gaming/reaction-tap';
import { ReactionPublisher } from '../gaming/reaction-publisher';
import { EndingPublication } from '../gaming/ending-publication';
import { RuntimeMediaProducer } from '../gaming/runtime-media';
import { GamingController } from '../gaming/controller';
import type { GamingControlProvider } from '../net/api';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { createAudioEngine, registerBuiltinAudioFilters } from '../audio';
import type { AudioEngine, LevelReading } from '../audio/api';
import { MASTER_STRIP } from '../audio/api';
import { computeGroupFrame, type ItemExtent } from '../core/geometry';
import { DEFAULT_SWARM_API_URL, DISCONNECT_PROTECTION_MS, type GroupItem, type Id, type Project, type SceneItem, type Source, type TransitionConfig, type Vec2 } from '../core/model';
import { Registry } from '../core/registry';
import type { Unsubscribe } from '../core/services';
import { createHealthMonitor } from '../health';
import type { HealthEvent, HealthIssue, HealthMonitor } from '../health/api';
import { createBroadcastHost, createSwarmPublisher, createWhipPublisher, publishChannelProfile, raidChannel } from '../net';
import type {
  BroadcastHost,
  BroadcastRoom,
  BroadcastStartOptions,
  CreateInviteOptions,
  InviteInfo,
  PeerStats,
  SwarmPublisher,
  SwarmStats,
  WhipPublisher,
} from '../net/api';
import { createRecorder } from '../output';
import type { Recorder, RecordingFormat, RecordingResult, ReplayBufferStartOptions } from '../output/api';
import { createReplayBuffer, type ReplayBuffer } from '../output/replayBuffer';
import { recordingsToPurge } from '../output/retention';
import { createAssetStore, createProjectBundler, createProjectRepository } from '../persist';
import type { AssetMeta, AssetPutOptions, AssetStore, ProjectBundler, ProjectRepository, ProjectSummary } from '../persist/api';
import { createPluginHost } from '../plugins';
import type { PluginHost } from '../plugins/api';
import { createCompositor, registerBuiltinTransitions, registerBuiltinVideoFilters } from '../render';
import type { Compositor, CompositorView, ViewTarget, CompositorDeps } from '../render/api';
import { createSourceManager, expectsContinuousFrames, hasFrameStats, registerBuiltinSources } from '../sources';
import type { SourceManager } from '../sources/api';
import { createCommands, createDefaultProject, createId, createProjectStore, queries } from '../state';
import type { Commands, ProjectQueries, ProjectStore, ProjectStoreState } from '../state/api';
import type {
  DockContribution,
  HotkeyAction,
  LoadedPluginInfo,
  Notification,
  PrompterLink,
  RuntimeState,
  SourceRuntimeInfo,
  StudioApi,
  StudioEvents,
} from './api';
import { hostRoomOptions, swarmGuestRoomWanted } from './guestRoom';
import { captionTargetFor, createCaptionEngine, type CaptionEngine } from './captions';
import { createLiveGuard } from './liveGuard';
import { createOperatorAlert } from './operatorAlert';
import { alwaysOnProgramSources, computeProgramMix } from './programMix';
import { publishTitleDraft, type TitleDraft } from './streamTitle';
import { Emitter, createLogger, errorMessage, shallowEqual } from './support';

const AUTOSAVE_DELAY_MS = 800;
const MAX_NOTIFICATIONS = 20;
const TBAR_AUDIO_RAMP_MS = 30;
const BROADCAST_START_TIMEOUT_MS = 20_000;
const SOURCE_FROZEN_MS = 5_000;
const ROOM_STORAGE_PREFIX = 'ohmyobs.room.';
const NO_LEVELS: Readonly<Record<string, LevelReading>> = Object.freeze({});

interface SavedRoom {
  signalingUrl: string;
  roomId: string;
  hostKey: string;
}

function extentOf(group: GroupItem, measure: (item: SceneItem) => ItemExtent): ItemExtent {
  const frame = computeGroupFrame(group.children, measure);
  return { size: frame.size, min: frame.min };
}

class Studio implements StudioApi {
  private ordinaryProgram?:OrdinaryProgramOwner;
  private ordinaryBlockedOutput?:string;
  private ordinaryRetirement?:{value:NonNullable<ReturnType<CreatorProgram['transferRetirement']>>;context:AudioContext;project:string};
  private closeOrdinary(){this.ordinaryProgram?.close();this.ordinaryProgram=undefined;this.ordinaryRetirement=undefined;this.ordinaryBlockedOutput=undefined;}
  private createOrdinary(){
    const saved=this.ordinaryRetirement;if(!saved||!this.options.gamingOrdinaryProgram)return;
    if(this.destroyed||this.audio.context!==saved.context||saved.context.state!=='running'||this.current().id!==saved.project||document.visibilityState!=='visible'){this.closeOrdinary();return;}
    let audio:ReturnType<NonNullable<AudioEngine['observeProgramOutput']>>|undefined;
    try{audio=this.audio.observeProgramOutput?.();if(!audio)return;const owner=this.options.gamingOrdinaryProgram({audio,current:()=>this.ordinaryRetirement===saved&&!this.destroyed&&this.audio.context===saved.context&&saved.context.state==='running'&&this.current().id===saved.project&&document.visibilityState==='visible'});owner.retireAttempt(saved.value);this.ordinaryProgram=owner;return owner;}catch{audio?.close();}
  }
  private ordinaryComposition(input:Parameters<NonNullable<CompositorDeps['composition']>>[0]){
    const identity=JSON.stringify([input.programFrameSourceId,input.programFrameSourceGeneration,input.compositorGeneration,input.sceneId]);
    // A failed audio/evidence owner must not insert another serial worklet on
    // every unchanged frame. Only a new actual output or retirement can retry.
    if(this.ordinaryProgram&&!this.ordinaryProgram.current())this.ordinaryBlockedOutput=identity;
    const frame=this.ordinaryProgram?.composition(input);if(frame)return frame;
    if(this.ordinaryProgram?.current())return;
    this.ordinaryProgram?.close();this.ordinaryProgram=undefined;
    const saved=this.ordinaryRetirement;if(!saved||this.ordinaryBlockedOutput===identity||input.sources.some(source=>source.source_id===saved.value.nativeSourceId))return;
    this.ordinaryBlockedOutput=undefined;return this.createOrdinary()?.composition(input);
  }
  readonly gaming: GamingController;
  readonly registry = new Registry();
  readonly project: ProjectStore;
  readonly runtime: StoreApi<RuntimeState>;
  readonly commands: Commands;
  readonly queries: ProjectQueries = queries;

  private readonly logger = createLogger();
  private readonly alert: ReturnType<typeof createOperatorAlert>;
  private readonly events = new Emitter<StudioEvents>();
  private readonly host: BroadcastHost;
  private readonly whip: WhipPublisher;
  private readonly swarm: SwarmPublisher;
  private readonly captions: CaptionEngine;
  private readonly recorder: Recorder;
  private readonly replay: ReplayBuffer;
  private readonly repository: ProjectRepository;
  private readonly assets: AssetStore;
  private readonly bundler: ProjectBundler;
  private readonly plugins: PluginHost;
  // Created in init(): the audio context takes its sample rate from the restored project,
  // and the source manager and compositor need that context.
  private audio!: AudioEngine;
  private sources!: SourceManager;
  private compositor!: Compositor;
  private health!: HealthMonitor;

  private programStream: MediaStream | null = null;
  private reconciled: Project | null = null;
  private readonly strips = new Set<Id>();
  private readonly attachedAudio = new Map<Id, AudioNode | null>();
  private readonly disposers: Unsubscribe[] = [];
  private initPromise: Promise<void> | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saveChain: Promise<void> = Promise.resolve();
  private midpointTimer: ReturnType<typeof setTimeout> | null = null;
  private transitionSeq = 0;
  private transitioning = false;
  /** A T-bar scrub in progress, kept so it can always be cancelled. */
  private manual: { toSceneId: Id; config: TransitionConfig } | null = null;
  /** Storage key of the room opened by the running broadcast. */
  private liveRoomKey: Id | null = null;
  private guestRoomOpening: Promise<void> | null = null;
  /**
   * The start of a recording this studio began with the broadcast (channel.saveBroadcasts), which
   * it stops with the stream. Null when none is running (cleared as soon as the recorder is idle).
   */
  private savedBroadcast: Promise<void> | null = null;
  /** Options the replay buffer was last started with; null while it is not wanted. */
  private replayOptions: ReplayBufferStartOptions | null = null;
  private syncingReplay = false;
  /** Retention purges run one after another. */
  private purgeChain: Promise<void> = Promise.resolve();
  private destroyed = false;

  constructor(private readonly options: StudioOptions = {}) {
    this.alert = createOperatorAlert(this.logger, { notifications: !options.embedded });
    registerBuiltinSources(this.registry);
    registerBuiltinVideoFilters(this.registry);
    registerBuiltinTransitions(this.registry);
    registerBuiltinAudioFilters(this.registry);

    const initial = createDefaultProject();
    this.project = createProjectStore(initial);
    this.commands = createCommands(this.project, this.registry);

    this.host = createBroadcastHost({ logger: this.logger });
    this.whip = createWhipPublisher({ logger: this.logger });
    this.swarm = createSwarmPublisher({ logger: this.logger });
    this.gaming = new GamingController({telemetry:options.gamingTelemetry,
      enabled: import.meta.env.VITE_GAMING_STUDIO_ENABLED === 'true',
      provider: options.embedded ? options.gamingProvider : undefined,
      storage: { getItem: key => window.sessionStorage.getItem(key), setItem: (key, value) => window.sessionStorage.setItem(key, value), removeItem: key => window.sessionStorage.removeItem(key) },
      liveStreamId: () => this.swarm.getState().status === 'live' ? this.swarm.getState().streamId : null,
    });
    this.captions = createCaptionEngine({ logger: this.logger, notify: (level, message) => this.notify(level, message) });
    this.recorder = createRecorder({ logger: this.logger });
    this.replay = createReplayBuffer({ logger: this.logger });
    this.repository = createProjectRepository();
    this.assets = createAssetStore();
    this.bundler = createProjectBundler(this.assets);
    this.plugins = createPluginHost({
      studio: this,
      getProgramStream: () => this.requireProgramStream(),
      getProgramSourceEvidence: () => this.getProgramSourceEvidence(),
      getAudioContext: () => this.audio.context,
      probeTexture: (source) => this.compositor?.probeTexture?.(source) ?? false,
      probeFilterFragment: (fragment, key, originSampler) => this.compositor?.probeFilterFragment?.(fragment, key, originSampler) ?? 'unavailable',
      logger: this.logger,
    });

    this.runtime = createStore<RuntimeState>(() => ({
      ready: false,
      fatalError: null,
      programSceneId: null,
      previewSceneId: null,
      studioMode: false,
      transition: { active: false, fromSceneId: null, toSceneId: null, progress: 0 },
      sources: {},
      audioContextState: 'suspended',
      broadcast: this.host.getState(),
      whip: this.whip.getState(),
      swarm: this.swarm.getState(),
      recording: this.recorder.getState(),
      replay: this.replay.getState(),
      captions: this.captions.getState(),
      guests: [],
      render: null,
      health: { status: 'idle', latest: null, issues: [], events: [] },
      network: {},
      collection: { id: initial.id, name: initial.name, dirty: false, lastSavedAt: null },
      plugins: [],
      docks: [],
      hotkeyActions: [],
      notifications: [],
      prompterLink: 'none',
      nativeOperational: false,
    }));
    if (this.gaming.enabled && options.gamingProvider) {
      const selected=options.gamingSelectedComposition;selected?.connect({controller:this.gaming,publisher:()=>this.swarm,enabled:()=>!this.destroyed&&import.meta.env.VITE_GAMING_STUDIO_ENABLED==='true'});
      const legacy=new RuntimeMediaProducer({controller:this.gaming,provider:options.gamingProvider,publisher:()=>this.swarm,mountedProducer:()=>this.gaming.program?.mountedProducer(),
        enabled:()=>!selected?.selected()&&!this.destroyed&&import.meta.env.VITE_GAMING_STUDIO_ENABLED==='true'});
      const producer=selected?{sync:()=>{legacy.sync();selected.sync();},waitReady:(session:{sessionId:string;revision:number},signal:AbortSignal)=>selected.selected()?selected.waitReady(session,signal):legacy.waitReady(session,signal),dispose:()=>{legacy.dispose();selected.dispose();}}:legacy;
      this.gaming.runtimeMedia=producer;
      this.disposers.push(()=>producer.dispose(),this.runtime.subscribe(()=>producer.sync()),this.events.on('program-stream-changed',()=>producer.sync()));
      const ending = new EndingPublication({telemetry:options.gamingTelemetry,
        controller: this.gaming, provider: options.gamingProvider, publisher: () => this.swarm,
        enabled: () => !selected?.selected() && !this.destroyed && import.meta.env.VITE_GAMING_STUDIO_ENABLED === 'true',
        restoredProgram:()=>this.gaming.program?.restoredProgram,
        scene: () => this.runtime.getState().programSceneId,
        track: () => this.programStream?.getVideoTracks()[0] ?? null,
        stream: () => this.swarm.getState().status === 'live' ? this.swarm.getState().streamId : null,
        storage: { getItem: key => window.sessionStorage.getItem(key), setItem: (key, value) => window.sessionStorage.setItem(key, value) },
      });
      this.disposers.push(() => ending.dispose(), this.runtime.subscribe(() => ending.sync()),
        this.events.on('program-stream-changed', () => ending.sync()));
    }
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  init(): Promise<void> {
    this.initPromise ??= this.start().catch((error: unknown) => {
      this.logger.log('error', 'studio', 'Initialisation failed', error);
      this.runtime.setState({ fatalError: errorMessage(error) });
      throw error;
    });
    return this.initPromise;
  }

  private async start(): Promise<void> {
    const project = await this.loadInitialProject();
    this.audio = createAudioEngine({ registry: this.registry, logger: this.logger, sampleRate: project.audio.sampleRate });
    await this.audio.init();

    this.sources = createSourceManager({
      registry: this.registry,
      context: {
        audioContext: this.audio.context,
        getVideoSettings: () => this.current().video,
        assets: this.assets,
        guests: this.host.guests,
        logger: this.logger,
      },
      onRuntimeState: (sourceId, state) => {
        if (this.current().sources[sourceId]) this.commands.setSourceRuntimeState(sourceId, state);
      },
      onSettingsMigrated: (sourceId, settings, settingsVersion) => {
        if (this.current().sources[sourceId]) this.commands.applyMigratedSettings(sourceId, settings, settingsVersion);
      },
    });
    if(this.gaming.enabled&&this.options.gamingProvider?.creatorRender){
      const creator=new CreatorSourceOwner({diagnose:reason=>this.logger.log('info','gaming.creator',`Creator preview retired: ${reason}`),source:new CreatorSource(this.sources,this.options.gamingProvider),session:()=>this.gaming.getState().session,project:()=>this.current(),rendererUrl:import.meta.env.VITE_GAMING_CREATOR_RENDERER_URL??''});
      this.gaming.creator=creator;this.disposers.push(()=>creator.dispose());
      const retire=()=>creator.stop();window.addEventListener('pagehide',retire);this.disposers.push(()=>window.removeEventListener('pagehide',retire));
    }
    if (import.meta.env.VITE_GAMING_REACTION_ENABLED === 'true' && this.options.embedded && this.options.gamingProvider?.reaction) {
      const provider = this.options.gamingProvider;
      let reaction: ReactionController;
      const tap = new ReactionTap({
        enabled: true, sources: this.sources, audio: this.audio,
        sourceKind: id => this.current().sources[id]?.kind,
        consentEpoch: medium => reaction?.consentEpoch(medium) ?? null,
        onLost: medium => { void reaction?.stopMedium(medium); },
      });
      const publishers = new Map<import('../gaming/reaction-tap').ReactionMedium, ReactionPublisher>();
      for (const medium of ['camera','microphone'] as const) {
        publishers.set(medium, createReactionSessionPublisher({
          apiBase: import.meta.env.VITE_GAMING_API_URL ?? '', medium, provider,
          session: () => this.gaming.getState().session,
          consentEpoch: () => reaction.consentEpoch(medium),
          onLost: () => { void reaction?.recoverMedium(medium).catch(() => undefined); },
        }));
      }
      reaction = new ReactionController({
        enabled: true, provider, session: () => this.gaming.getState().session,
        sources: () => Object.values(this.current().sources).filter(source => ['camera','audio-input'].includes(source.kind) && this.sources.getSnapshot(source.id)?.status.state === 'live').map(source => ({ id: source.id, name: source.name, kind: source.kind })),
        tap, publisher: medium => publishers.get(medium)!,
      });
      this.gaming.reaction = reaction;
    }
    this.compositor = createCompositor({
      registry: this.registry,
      getProject: () => this.effectiveProgramProject(),
      getSourceKind:id=>this.sources.getKind?.(id),
      composition:input=>this.ordinaryComposition(input)??this.gaming.program?.composition(input),
      getSourceVideo: (sourceId) => this.sources.getVideo(sourceId),
      onProgramFrame: (receipt) => this.events.emit('program-frame',receipt),
      onBeforeFrame: (time) => {
        this.sources.tick(time);
        this.events.emit('frame', { timeMs: time.timeMs, frame: time.frame });
      },
      assets: this.assets,
      logger: this.logger,
    });
    if(this.gaming.creator){
      const selection=()=>{const reaction=this.gaming.reaction;const selected=(medium:'camera'|'microphone')=>{const s=reaction?.getState()[medium],epoch=reaction?.consentEpoch(medium);return s?.consent&&s.active&&epoch!==null&&epoch!==undefined?{sourceId:s.sourceId,consentEpoch:epoch}:'absent' as const;};return {camera:selected('camera'),microphone:selected('microphone')};};
      const program=new CreatorProgram({started:()=>this.closeOrdinary(),retired:this.options.gamingOrdinaryProgram?value=>{
        this.closeOrdinary();this.ordinaryRetirement={value:Object.freeze({...value}),context:this.audio.context,project:this.current().id};this.createOrdinary();
      }:undefined,qualify:this.options.gamingCreatorQualification?async session=>{
        const qualifier=this.options.gamingCreatorQualification,owner=this.gaming.creator;
        const mounted=owner?.mounted(),audio=this.audio.context,projectId=this.current().id;
        const trustedSession=this.gaming.getState().session;
        if(!qualifier||!mounted||!trustedSession||(['sessionId','streamId','epoch','transitionId','revision','versionId','state'] as const).some(key=>session[key]!==trustedSession[key]))throw Error('creator_program_unavailable');
        const pinnedSession=Object.freeze({...trustedSession}),lease=mounted.lease,producerId=mounted.producerId;
        const sourceId=lease.sourceId,renderId=lease.renderId,captureGeneration=lease.captureGeneration,origin=lease.observeClock?.()?.originId;
        const connection=lease.connection??'absent',sampleRate=audio.sampleRate;
        const connectionIdentity=connection==='absent'?undefined:connection.identity;
        const connectionGeneration=connection==='absent'?undefined:connection.generation;
        let revoked=false;
        const localCurrent=()=>{
          if(revoked)return false;
          try{
            const current=this.gaming.getState().session,next=owner?.mounted();
            const nextConnection=next?.lease.connection??'absent';
            const sessionCurrent=!!current&&current.sessionId===pinnedSession.sessionId&&
              current.streamId===pinnedSession.streamId&&current.epoch===pinnedSession.epoch&&
              current.transitionId===pinnedSession.transitionId&&current.versionId===pinnedSession.versionId&&!current.closeRequest&&
              ['PREPARING','ACTIVE'].includes(current.state);
            const sourceCurrent=!!next&&next.lease===lease&&next.producerId===producerId&&
              lease.sourceId===sourceId&&lease.renderId===renderId&&lease.captureGeneration===captureGeneration&&lease.current()&&
              !!origin&&lease.observeClock?.()?.originId===origin;
            const connectionCurrent=nextConnection===connection&&(connection==='absent'||
              (connection.identity===connectionIdentity&&connection.generation===connectionGeneration&&connection.current()));
            revoked=this.destroyed||this.options.gamingCreatorQualification!==qualifier||
              this.gaming.creator!==owner||!sessionCurrent||this.current().id!==projectId||
              !sourceCurrent||!connectionCurrent||this.audio.context!==audio||audio.sampleRate!==sampleRate||
              audio.state!=='running'||document.visibilityState!=='visible';
          }catch{revoked=true;}
          return !revoked;
        };
        const context:CreatorQualificationContext=Object.freeze({
          session:pinnedSession,programSourceId:()=>this.swarm.getWitnessIdentity?.()?.sourceId,
          project:()=>this.current(),
          mounted:()=>{const value=owner?.mounted();return value?Object.freeze({...value,connection:value.lease.connection??'absent'}):undefined;},
          audio:()=>{const value=this.audio.context;return Object.freeze({identity:value,state:value.state,sampleRate:value.sampleRate,currentFrame:Math.floor(value.currentTime*value.sampleRate)});},
          visible:()=>document.visibilityState==='visible',
          current:localCurrent,
        });
        if(!localCurrent())throw Error('creator_program_unavailable');
        const proof=fenceCreatorQualification(await qualifier(context),localCurrent);
        if(!proof.current())throw Error('creator_program_unavailable');
        return proof;
      }:undefined,owner:this.gaming.creator,project:()=>this.current(),scene:()=>this.runtime.getState().programSceneId,transition:id=>this.transition(id),audio:this.audio,sources:this.sources,selection,settled:()=>!this.transitioning&&!this.compositor.getTransitionState().active,changed:()=>this.refreshActive()});
      this.gaming.program=program;this.disposers.push(()=>program.dispose(),this.gaming.creator.subscribe(()=>{if(this.gaming.creator?.getState()==='source-created')void program.prepare().catch(()=>undefined);else program.sync();}));
      if(this.gaming.reaction)this.disposers.push(this.gaming.reaction.subscribe(()=>program.sync()));
    }
    await this.compositor.init();
    this.compositor.setDegradationPolicy({ auto: true, minRenderScale: 0.5 });

    this.wireModules();
    this.openProject(project);
    this.compositor.start();
    this.setProgramStream(this.compositor.getProgramStream());

    this.disposers.push(this.project.subscribe((state) => this.onProjectState(state)));
    const flush = () => void this.flushSave();
    window.addEventListener('pagehide', flush);
    this.disposers.push(() => window.removeEventListener('pagehide', flush));
    this.startHealthMonitor();
    const guard = createLiveGuard(() => this.isLive(), this.logger, { beforeUnload: !this.options.embedded });
    this.disposers.push(guard.dispose, this.runtime.subscribe(guard.refresh));

    this.disposers.push(
      this.project.subscribe((state, prev) => {
        const next = state.project;
        const before = prev.project;
        if (next.channel.vodRetentionDays !== before.channel.vodRetentionDays) void this.purgeOldRecordings();
        if (next.channel.clips !== before.channel.clips || next.output.recording !== before.output.recording) this.syncReplayBuffer();
        if (next.channel.clips.enabled !== before.channel.clips.enabled) this.swarm.setClips(next.channel.clips.enabled);
        if (next.output.streaming !== before.output.streaming) this.syncCaptions();
      }),
    );

    this.runtime.setState({ ready: true, audioContextState: this.audio.getState() });
    void this.purgeOldRecordings();
    await this.syncPlugins(project);
  }

  private wireModules(): void {
    this.disposers.push(
      this.sources.subscribe((sourceId) => this.onSourceChanged(sourceId)),
      this.compositor.onStats((render) => this.runtime.setState({ render })),
      this.compositor.onTransitionState((transition) => this.runtime.setState({ transition })),
      this.compositor.onProgramStreamChanged((stream) => this.setProgramStream(stream)),
      this.audio.onStateChange((audioContextState) => this.runtime.setState({ audioContextState })),
      this.host.subscribe((broadcast) => {
        this.reportOutputError('broadcast', 'Streaming', this.runtime.getState().broadcast.error, broadcast.error);
        this.runtime.setState({ broadcast });
        this.events.emit('broadcast-state', broadcast);
      }),
      this.host.guests.subscribe(() => this.runtime.setState({ guests: this.host.guests.list() })),
      this.whip.subscribe((whip) => {
        this.reportOutputError('whip', 'WHIP streaming', this.runtime.getState().whip.error, whip.error);
        this.runtime.setState({ whip });
      }),
      this.swarm.subscribe((swarm) => {
        const before = this.runtime.getState().swarm.status;
        this.reportOutputError('swarm', 'Swarm streaming', this.runtime.getState().swarm.error, swarm.error);
        this.runtime.setState({ swarm });
        this.gaming.syncBroadcast();
        const ended = swarm.status === 'idle' || swarm.status === 'error';
        if (ended && before !== swarm.status && this.current().output.streaming.mode === 'swarm') this.closeSwarmGuestRoom();
      }),
      this.recorder.subscribe((recording) => {
        this.reportOutputError('recording', 'Recording', this.runtime.getState().recording.error, recording.error);
        // Whatever stopped the recorder (the operator, an error), the saved broadcast is over: a
        // recording the operator starts afterwards is theirs and must not be stopped with the stream.
        if (recording.status === 'idle') this.savedBroadcast = null;
        this.runtime.setState({ recording });
        this.events.emit('recording-state', recording);
      }),
      this.replay.subscribe((replay) => {
        const before = this.runtime.getState().replay.error;
        if (replay.error && replay.error !== before) this.notify('warn', `Clips are unavailable: ${replay.error}`);
        this.runtime.setState({ replay });
      }),
      this.captions.subscribe((captions) => this.runtime.setState({ captions })),
      // Outputs starting and stopping (and anything else live) decide whether the buffer runs.
      this.runtime.subscribe(() => this.syncReplayBuffer()),
      this.runtime.subscribe(() => this.syncCaptions()),
      this.plugins.subscribe(() => this.refreshPluginList()),
    );
    const offScale = this.compositor.onSourceScale?.((sourceId, scale) => this.sources.setRasterScale(sourceId, scale));
    if (offScale) this.disposers.push(offScale);
  }

  async destroy(): Promise<void> {
    if (this.destroyed) return;
    this.destroyed = true;
    this.closeOrdinary();
    this.gaming.dispose();
    // Let a start-up in flight finish so everything it creates is torn down below.
    await this.initPromise?.catch(() => undefined);
    await this.flushSave();
    this.health?.destroy();
    this.alert.dispose();
    if (this.midpointTimer) clearTimeout(this.midpointTimer);
    for (const dispose of this.disposers.splice(0)) dispose();
    const settle = async (label: string, work: () => unknown) => {
      try {
        await work();
      } catch (error) {
        this.logger.log('warn', 'studio', `Cleanup step "${label}" failed`, error);
      }
    };
    await settle('plugins', () => this.plugins.destroy());
    this.replay.stop();
    this.captions.stop();
    if (this.recorder.getState().status !== 'idle') await settle('recorder', () => this.recorder.stop());
    await settle('broadcast', () => this.host.stop());
    await settle('whip', () => this.whip.stop());
    await settle('swarm', () => this.swarm.stop());
    if (this.compositor) await settle('compositor', () => this.compositor.destroy());
    if (this.sources) await settle('sources', () => this.sources.destroy());
    if (this.audio) await settle('audio', () => this.audio.destroy());
    this.assets.dispose();
    this.events.clear();
  }

  on<K extends keyof StudioEvents>(event: K, listener: (payload: StudioEvents[K]) => void): Unsubscribe {
    return this.events.on(event, listener);
  }

  private isLive(): boolean {
    const { broadcast, whip, swarm, recording } = this.runtime.getState();
    return (
      broadcast.status !== 'idle' ||
      whip.status !== 'idle' ||
      swarm.status !== 'idle' ||
      recording.status === 'recording' ||
      recording.status === 'paused'
    );
  }

  async resumeAudio(): Promise<void> {
    if (!this.audio) return;
    await this.audio.resume();
    this.runtime.setState({ audioContextState: this.audio.getState() });
  }

  // -------------------------------------------------------------------------
  // Document -> live modules
  // -------------------------------------------------------------------------

  private current(): Project {
    return this.project.getState().project;
  }

  private onProjectState(state: ProjectStoreState): void {
    if (state.project === this.reconciled) return;
    this.reconcile(state.project, this.reconciled);
    this.scheduleSave();
  }

  private openProject(project: Project): void {
    // A transition from the previous collection must not commit into this one.
    this.manual = null;
    this.cancelTransition();
    this.project.replace(project);
    const opened = this.current();
    this.runtime.setState({
      programSceneId: null,
      previewSceneId: null,
      collection: { id: opened.id, name: opened.name, dirty: false, lastSavedAt: null },
    });
    this.reconcile(opened, null);
    void this.repository.setLastOpenedId(opened.id);
    this.events.emit('project-opened', { id: opened.id });
  }

  private reconcile(project: Project, previous: Project | null): void {
    this.reconciled = project;
    this.sources.sync(project);
    this.gaming.creator?.sync();this.gaming.program?.sync();
    if (previous?.video !== project.video) this.compositor.applyVideoSettings(project.video);
    if (previous?.audio !== project.audio) this.audio.applySettings(project.audio);
    if (previous && previous.output.streaming !== project.output.streaming) this.applyLiveEncoding(project);

    for (const [id, source] of Object.entries(project.sources)) {
      this.syncStrip(id, source, previous?.sources[id]);
      this.refreshSourceInfo(id);
    }
    for (const id of [...this.strips]) {
      if (!project.sources[id]) this.dropStrip(id);
    }
    const infos = this.runtime.getState().sources;
    const stale = Object.keys(infos).filter((id) => !project.sources[id]);
    if (stale.length > 0) {
      const next = { ...infos };
      for (const id of stale) delete next[id];
      this.runtime.setState({ sources: next });
    }

    const collection = this.runtime.getState().collection;
    if (collection.name !== project.name) this.runtime.setState({ collection: { ...collection, name: project.name } });

    this.ensureSceneSelection(project);
    this.refreshActive();
  }

  /**
   * Push encoding settings into a running stream. P2P renegotiates a codec change live; WHIP applies it
   * from the next session; swarm mode applies bitrates at the next segment boundary (always H.264).
   */
  private applyLiveEncoding(project: Project): void {
    const { videoKbps, audioKbps, codec, degradation } = project.output.streaming;
    const encoding = { videoKbps, audioKbps, codec, degradation };
    const { broadcast, whip, swarm } = this.runtime.getState();
    const report = (error: unknown) => this.notify('warn', `Could not apply stream settings live: ${errorMessage(error)}`);
    if (broadcast.status === 'live' || broadcast.status === 'reconnecting') this.host.updateEncoding(encoding).catch(report);
    if (whip.status === 'live' || whip.status === 'reconnecting') this.whip.updateEncoding(encoding).catch(report);
    if (swarm.status === 'live' || swarm.status === 'reconnecting') this.swarm.updateEncoding(encoding).catch(report);
  }

  /** Keep the audio strip for a source in step with the document and the live node. */
  private syncStrip(id: Id, source: Source, previous: Source | undefined): boolean {
    const node = this.sources.getAudio(id);
    const audible = node !== null || this.registry.getSource(source.kind)?.caps.audio === true;
    if (!audible) {
      if (this.strips.has(id)) this.dropStrip(id);
      return false;
    }
    const isNew = !this.strips.has(id);
    if (isNew || previous?.audio !== source.audio) this.audio.setStrip(id, source.audio);
    if (isNew || previous?.filters !== source.filters) this.audio.setFilters(id, source.filters);
    this.strips.add(id);
    if (!this.attachedAudio.has(id) || this.attachedAudio.get(id) !== node) {
      this.audio.attachSource(id, node);
      this.attachedAudio.set(id, node);
    }
    return isNew;
  }

  private dropStrip(id: Id): void {
    this.audio.removeSource(id);
    this.strips.delete(id);
    this.attachedAudio.delete(id);
  }

  private onSourceChanged(sourceId: Id): void {
    const source = this.current().sources[sourceId];
    if (!source) return;
    // A strip created after the mix was last set starts silent until the mix is re-applied.
    if (this.syncStrip(sourceId, source, source)) this.refreshActive();
    this.refreshSourceInfo(sourceId);
  }

  private refreshSourceInfo(id: Id): void {
    const snapshot = this.sources.getSnapshot(id);
    if (!snapshot) return;
    const info: SourceRuntimeInfo = {
      status: snapshot.status,
      hasVideo: snapshot.hasVideo,
      hasAudio: snapshot.hasAudio,
      width: snapshot.width,
      height: snapshot.height,
    };
    if (snapshot.status.detail) info.detail = snapshot.status.detail;
    if (snapshot.live) info.live = snapshot.live;
    if (snapshot.media) info.media = snapshot.media;
    const infos = this.runtime.getState().sources;
    const before = infos[id];
    if (before && sameSourceInfo(before, info)) return;
    this.runtime.setState({ sources: { ...infos, [id]: info } });
  }

  /** Live info and media position change without notifications; poll the sources that report them. */
  private refreshPolledSourceInfo(): void {
    for (const id of Object.keys(this.current().sources)) {
      const runtime = this.sources.get(id);
      if (runtime?.getLiveInfo || runtime?.getMediaState) this.refreshSourceInfo(id);
    }
  }

  private effectiveProgramProject(){return this.gaming.program?.effective(this.current())??this.current();}

  private ensureSceneSelection(project: Project): void {
    project=this.gaming.program?.effective(project)??project;
    const state = this.runtime.getState();
    const fallback = project.scenes[project.currentSceneId] ? project.currentSceneId : project.sceneOrder[0] ?? null;
    let program = state.programSceneId;
    if (!program || !project.scenes[program]) {
      program = fallback;
      if (program) this.compositor.setProgramScene(program);
    }
    if (program && !project.scenes[project.currentSceneId]) {
      const repaired = program;
      queueMicrotask(() => {
        if (this.current().scenes[repaired] && !this.current().scenes[this.current().currentSceneId]) {
          this.commands.setCurrentScene(repaired);
        }
      });
    }
    let preview = state.previewSceneId;
    if (!state.studioMode || !preview || !project.scenes[preview]) preview = program;
    if (program !== state.programSceneId || preview !== state.previewSceneId) {
      this.runtime.setState({ programSceneId: program, previewSceneId: preview });
      this.compositor.setPreviewScene(state.studioMode ? preview : null);
    }
  }

  /**
   * Tell sources whether they are on program (active) or drawn anywhere (showing) and, outside
   * transitions, settle the audio mix. Only visible items count, so hiding an item stops its
   * source's ticking and program-only work; whether a capture source also lets go of its device
   * is the source's own opt-in (see SourceRuntime.setShowing), so unhiding stays instant.
   * `extraSceneId` is a scene entering program through a transition.
   */
  private refreshActive(extraSceneId?: Id): void {
    const project = this.effectiveProgramProject();
    const { programSceneId, previewSceneId } = this.runtime.getState();
    const visibleIn = (sceneId: Id | null | undefined): Set<Id> =>
      sceneId && project.scenes[sceneId] ? queries.sourcesInScene(project, sceneId, { visibleOnly: true }) : new Set<Id>();
    // Always-on-program audio sources are active in every scene (SRC-D-015).
    const program = new Set([...visibleIn(programSceneId), ...visibleIn(extraSceneId), ...alwaysOnProgramSources(project)]);
    const showing = new Set([...program, ...visibleIn(previewSceneId)]);
    for (const target of this.openViews) {
      if (target.type === 'scene') for (const id of visibleIn(target.sceneId)) showing.add(id);
      else if (target.type === 'source' && project.sources[target.sourceId]) showing.add(target.sourceId);
    }
    this.sources.setActive(program, showing);
    if (!this.transitioning) {
      this.audio.setProgramMix(this.gaming.program?.mix(computeProgramMix(project, queries, programSceneId, null, 0))??computeProgramMix(project, queries, programSceneId, null, 0), 0);
    }
  }

  // -------------------------------------------------------------------------
  // Program / preview
  // -------------------------------------------------------------------------

  selectScene(sceneId: Id): void {
    if (!this.current().scenes[sceneId]) return;
    if (this.runtime.getState().studioMode) {
      this.cancelManualTransition();
      this.runtime.setState({ previewSceneId: sceneId });
      this.compositor.setPreviewScene(sceneId);
      this.refreshActive();
      return;
    }
    this.transition(sceneId).catch((error: unknown) => this.notify('error', `Transition failed: ${errorMessage(error)}`));
  }

  setStudioMode(enabled: boolean): void {
    const state = this.runtime.getState();
    if (state.studioMode === enabled) return;
    this.cancelManualTransition();
    this.runtime.setState({ studioMode: enabled, previewSceneId: state.programSceneId });
    this.compositor.setPreviewScene(enabled ? state.programSceneId : null);
    this.refreshActive();
  }

  private currentTransition(
    project: Project,
    transitionId?: Id,
  ): { config: TransitionConfig | undefined; instant: boolean; midpointAudio: boolean } {
    const config = project.transitions[transitionId ?? project.currentTransitionId];
    const definition = config ? this.registry.getTransition(config.kind) : undefined;
    return {
      config,
      instant: !config || !definition || definition.fragment === null,
      midpointAudio: definition?.audio === 'cut-at-midpoint',
    };
  }

  async transition(toSceneId?: Id, options?: { transitionId?: Id }): Promise<void> {
    const state = this.runtime.getState();
    const project = this.effectiveProgramProject();
    const target = toSceneId ?? (state.studioMode ? state.previewSceneId : null);
    if (!target || !project.scenes[target]) return;
    const running = this.compositor.getTransitionState();
    // A second request for the scene already being transitioned to (a double
    // click) must not cut the running transition short.
    if (running.active && running.toSceneId === target && !this.manual) return;
    this.cancelManualTransition();
    // Interrupting a transition settles it on its target first, so that is where this one starts.
    const from = running.active && running.toSceneId ? running.toSceneId : this.compositor.getProgramScene();
    if (target === from && !running.active) return;

    const { config, instant, midpointAudio } = this.currentTransition(project, options?.transitionId);
    const durationMs = instant ? 0 : project.transitionDurationMs;
    this.cancelTransition();
    const seq = this.transitionSeq;
    this.transitioning = true;
    this.refreshActive(target);

    const targetMix = this.gaming.program?.mix(computeProgramMix(project, queries, target, null, 0))??computeProgramMix(project, queries, target, null, 0);
    if (durationMs > 0 && midpointAudio) {
      this.midpointTimer = setTimeout(() => this.audio.setProgramMix(targetMix, 0), durationMs / 2);
    } else {
      this.audio.setProgramMix(targetMix, durationMs);
    }
    this.events.emit('transition-started', { fromSceneId: from, toSceneId: target });

    try {
      if (instant || !config) this.compositor.setProgramScene(target);
      else await this.compositor.startTransition(target, config, durationMs);
    } catch (error) {
      // A newer transition replaced this one; it owns the outcome.
      if (seq !== this.transitionSeq) return;
      this.compositor.setProgramScene(target);
      this.logger.log('warn', 'studio', 'Transition failed; cut instead', error);
    }
    if (seq === this.transitionSeq) this.commitProgram(target, from);
  }

  setTBar(progress: number | null): void {
    // Cancelling must work even if the conditions for scrubbing no longer hold.
    if (progress === null) {
      this.cancelManualTransition();
      return;
    }
    const state = this.runtime.getState();
    const from = state.programSceneId;
    const to = state.previewSceneId;
    if (!state.studioMode || !from || !to || from === to) return;
    const project = this.current();
    const { config } = this.currentTransition(project);
    if (!config) return;

    if (progress >= 1) {
      this.compositor.setManualTransition(to, config, 1);
      this.manual = null;
      this.cancelTransition();
      this.commitProgram(to, from);
      return;
    }
    const starting = !this.manual;
    if (starting) this.cancelTransition();
    this.manual = { toSceneId: to, config };
    this.transitioning = true;
    this.compositor.setManualTransition(to, config, Math.max(0, progress));
    // The incoming scene is on program from the first scrub step.
    if (starting) this.refreshActive(to);
    this.audio.setProgramMix(computeProgramMix(project, queries, from, to, progress), TBAR_AUDIO_RAMP_MS);
  }

  /** Invalidate any timed transition in flight: its completion no longer commits anything. */
  private cancelTransition(): void {
    this.transitionSeq += 1;
    if (this.midpointTimer) {
      clearTimeout(this.midpointTimer);
      this.midpointTimer = null;
    }
    this.transitioning = false;
  }

  private cancelManualTransition(): void {
    if (!this.manual) return;
    const { toSceneId, config } = this.manual;
    this.manual = null;
    this.compositor.setManualTransition(toSceneId, config, null);
    this.transitioning = false;
    this.refreshActive();
  }

  private commitProgram(target: Id, previous: Id | null): void {
    if (this.midpointTimer) {
      clearTimeout(this.midpointTimer);
      this.midpointTimer = null;
    }
    this.transitioning = false;
    const state = this.runtime.getState();
    // Taking preview to program swaps the two, so the previous program is ready to go back to.
    const swapped = state.studioMode && state.previewSceneId === target && previous ? previous : state.previewSceneId;
    const preview = state.studioMode ? swapped : target;
    this.runtime.setState({ programSceneId: target, previewSceneId: preview });
    this.compositor.setPreviewScene(state.studioMode ? preview : null);
    if (!this.gaming.program?.ownsScene(target)&&this.current().currentSceneId !== target) this.commands.setCurrentScene(target);
    this.refreshActive();
    this.gaming.program?.sync();
    this.events.emit('scene-changed', { programSceneId: target });
  }

  // -------------------------------------------------------------------------
  // Views, meters, sources
  // -------------------------------------------------------------------------

  /** Scene and source views (multiview, projectors, property previews) whose sources count as showing. */
  private readonly openViews: ViewTarget[] = [];

  createView(target: ViewTarget): CompositorView {
    const view = this.compositor.createView(target);
    if (target.type !== 'scene' && target.type !== 'source') return view;
    this.openViews.push(target);
    this.refreshActive();
    let open = true;
    return {
      get canvas() {
        return view.canvas;
      },
      target: view.target,
      setSize: (width, height) => view.setSize(width, height),
      setMaxFps: (fps) => view.setMaxFps(fps),
      destroy: () => {
        view.destroy();
        if (!open) return;
        open = false;
        this.openViews.splice(this.openViews.indexOf(target), 1);
        if (!this.destroyed) this.refreshActive();
      },
    };
  }

  getLevels(): Readonly<Record<string, LevelReading>> {
    return this.audio ? this.audio.getLevels() : NO_LEVELS;
  }

  getItemContentSize(sceneId: Id, itemId: Id): Vec2 {
    const location = queries.findItem(this.current(), sceneId, itemId);
    return location ? this.itemSize(location.item) : { x: 0, y: 0 };
  }

  private itemSize(item: SceneItem): Vec2 {
    if (item.type === 'source') {
      const snapshot = this.sources.getSnapshot(item.sourceId);
      return { x: snapshot?.width ?? 0, y: snapshot?.height ?? 0 };
    }
    if (item.type === 'scene') {
      const { width, height } = this.current().video;
      return { x: width, y: height };
    }
    // The size a group is laid out with is its frame (children's extent unioned with the origin).
    const measure = (child: SceneItem): ItemExtent =>
      child.type === 'group' ? extentOf(child, measure) : { size: this.itemSize(child) };
    return computeGroupFrame(item.children, measure).size;
  }

  screenshot(target: ViewTarget): Promise<Blob> {
    return this.compositor.captureFrame(target);
  }

  async invokeSource(sourceId: Id, action: string, args?: unknown): Promise<void> {
    await this.sources.get(sourceId)?.invoke?.(action, args);
  }

  // -------------------------------------------------------------------------
  // Outputs
  // -------------------------------------------------------------------------

  getProgramStream(): MediaStream | null {
    return this.programStream;
  }

  getProgramSourceEvidence(): import('../render/api').ProgramSourceEvidence | null {
    return this.compositor?.getProgramSourceEvidence?.() ?? null;
  }

  private requireProgramStream(): MediaStream {
    if (!this.programStream) throw new Error('The program output is not ready yet.');
    return this.programStream;
  }

  /** Combine the compositor's video track with the mixer's audio track into the one program stream. */
  private setProgramStream(video: MediaStream): void {
    const replacing = this.programStream !== null;
    const stream = new MediaStream([...video.getVideoTracks(), ...this.audio.getOutputStream().getAudioTracks()]);
    this.programStream = stream;
    this.events.emit('program-stream-changed', { stream });
    if (!replacing) return;
    const { broadcast, whip, swarm, recording } = this.runtime.getState();
    const report = (error: unknown) => this.notify('error', `Could not switch the live output: ${errorMessage(error)}`);
    if (broadcast.status !== 'idle') this.host.replaceStream(stream).catch(report);
    if (whip.status !== 'idle') this.whip.replaceStream(stream).catch(report);
    if (swarm.status !== 'idle') this.swarm.replaceStream(stream).catch(report);
    if (recording.status === 'recording' || recording.status === 'paused') {
      this.notify('warn', 'Video settings changed while recording; the recording was finalised.');
      this.recorder.stop().catch(report);
    }
    this.syncReplayBuffer();
  }

  async startBroadcast(): Promise<void> {
    await this.resumeAudio();
    const project = this.current();
    const settings = project.output.streaming;
    const stream = this.requireProgramStream();
    const encoding = {
      videoKbps: settings.videoKbps,
      audioKbps: settings.audioKbps,
      codec: settings.codec,
      degradation: settings.degradation,
    };

    if (settings.mode === 'whip') {
      const endpoint = settings.whipEndpoint.trim();
      if (!endpoint) throw new Error('Set a WHIP endpoint in Settings → Stream first.');
      await this.whip.start({
        ...encoding,
        endpoint,
        bearerToken: settings.whipBearerToken || undefined,
        stream,
        iceServers: settings.iceServers,
      });
      this.startSavedBroadcast();
      return;
    }

    if (settings.mode === 'swarm') {
      const streamKey = settings.swarmStreamKey.trim();
      if (!streamKey) throw new Error('Paste your ompeer.com stream key in Settings → Stream first.');
      await publishTitleDraft(this.titleDraft, (title) => this.swarm.start({
        apiUrl: settings.swarmApiUrl.trim(),
        streamKey,
        stream,
        segmentMs: settings.swarmSegmentMs,
        codec: settings.codec,
        h264Fallback: settings.swarmH264Fallback,
        width: project.video.width,
        height: project.video.height,
        fps: project.video.fps,
        videoKbps: settings.videoKbps,
        audioKbps: settings.audioKbps,
        iceServers: settings.iceServers,
        title,
        category: project.channel.info.category || undefined,
        tags: project.channel.info.tags,
        contentLabels: project.channel.info.contentLabels,
        language: project.channel.info.language || undefined,
        disconnectGraceMs: project.channel.disconnectProtection ? DISCONNECT_PROTECTION_MS : undefined,
        clips: project.channel.clips.enabled,
      }));
      this.startSavedBroadcast();
      await this.openSwarmGuestRoom();
      return;
    }

    await this.openHostRoom();
    this.startSavedBroadcast();
  }

  /**
   * Opens the signalling room on `signalingUrl`: viewers and guests in p2p mode, guests only in
   * swarm mode (see `hostRoomOptions`). The room is remembered only while a broadcast is running,
   * so a reload can reclaim it; stopping the broadcast retires the links.
   */
  private async openHostRoom(): Promise<BroadcastRoom> {
    const project = this.current();
    const settings = project.output.streaming;
    const options = hostRoomOptions(settings, this.requireProgramStream());
    const saved = readSavedRoom(project.id, settings.signalingUrl);
    // A server that accepts the socket but never opens the room (for example one
    // running an older protocol) must surface as an error, not an endless "connecting".
    const open = async (startOptions: BroadcastStartOptions) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error('The signalling server did not open a room in time. Check its URL and that it is up to date.')),
          BROADCAST_START_TIMEOUT_MS,
        );
      });
      try {
        return await Promise.race([this.host.start(startOptions), timeout]);
      } catch (error) {
        await this.host.stop().catch(() => undefined);
        throw error;
      } finally {
        clearTimeout(timer);
      }
    };
    let room: BroadcastRoom;
    try {
      room = await open(saved ? { ...options, roomId: saved.roomId, hostKey: saved.hostKey } : options);
    } catch (error) {
      if (!saved) throw error;
      // The remembered room may have expired on the server; open a fresh one.
      clearSavedRoom(project.id);
      room = await open(options);
    }
    this.liveRoomKey = project.id;
    writeSavedRoom(project.id, { signalingUrl: settings.signalingUrl, roomId: room.roomId, hostKey: room.hostKey });
    return room;
  }

  /**
   * Stream Together on the swarm: guests join through the same signalling room as in p2p mode,
   * and the swarm carries whatever of them is on program. A room that cannot open is a warning,
   * never a reason to take the stream down.
   */
  private async openSwarmGuestRoom(): Promise<void> {
    const { swarm, broadcast } = this.runtime.getState();
    if (!swarmGuestRoomWanted(this.current().output.streaming, { swarm: swarm.status, broadcast: broadcast.status })) return;
    this.guestRoomOpening ??= (async () => {
      try {
        if (broadcast.status === 'error') await this.host.stop();
        await this.openHostRoom();
      } catch (error) {
        this.notify('warn', `Guests cannot join this stream: ${errorMessage(error)}`);
      } finally {
        this.guestRoomOpening = null;
      }
    })();
    await this.guestRoomOpening;
  }

  /** The swarm ended on its own (error, ended from the server): its guest room goes with it. */
  private closeSwarmGuestRoom(): void {
    if (this.runtime.getState().broadcast.status === 'idle') return;
    this.host.stop().catch((error: unknown) => this.logger.log('warn', 'studio', 'Closing the guest room failed', error));
    if (this.liveRoomKey) clearSavedRoom(this.liveRoomKey);
    this.liveRoomKey = null;
  }

  /**
   * "Save past broadcasts": a recording that runs for as long as the stream does. Only started
   * when nothing is recording already, and only that recording is stopped with the stream.
   */
  private startSavedBroadcast(): void {
    if (!this.current().channel.saveBroadcasts || this.runtime.getState().recording.status !== 'idle' || this.savedBroadcast) return;
    const start = this.startRecording().catch((err: unknown) => {
      if (this.savedBroadcast === start) this.savedBroadcast = null;
      this.notify('warn', `The broadcast is not being saved: ${errorMessage(err)}`);
    });
    this.savedBroadcast = start;
  }

  /** Waits for a saved broadcast still starting, so a quick stop cannot leave it running. */
  private async stopSavedBroadcast(): Promise<void> {
    const start = this.savedBroadcast;
    if (!start) return;
    await start;
    if (this.savedBroadcast !== start) return;
    this.savedBroadcast = null;
    const status = this.runtime.getState().recording.status;
    if (status === 'recording' || status === 'paused') {
      await this.recorder.stop().catch((err: unknown) => this.notify('warn', `Could not finish saving the broadcast: ${errorMessage(err)}`));
    }
  }

  /** The title typed in this session's forms, until ompeer.com has it. */
  private readonly titleDraft: TitleDraft = {
    read: () => this.current().channel.info.title,
    clear: () => this.commands.setStreamInfo({ title: '' }),
  };

  async updateStreamInfo(): Promise<void> {
    const project = this.current();
    if (project.output.streaming.mode !== 'swarm') return;
    const status = this.runtime.getState().swarm.status;
    if (status !== 'live' && status !== 'reconnecting') return;
    const info = project.channel.info;
    await publishTitleDraft(this.titleDraft, (title) => this.swarm.updateInfo({ title, category: info.category, tags: info.tags, language: info.language, contentLabels: info.contentLabels }));
  }

  async publishChannelProfile(): Promise<void> {
    const project = this.current();
    const streamKey = project.output.streaming.swarmStreamKey.trim();
    if (!streamKey) throw new Error('Paste your ompeer.com stream key in Settings → Stream first.');
    await publishChannelProfile({
      apiUrl: project.output.streaming.swarmApiUrl,
      streamKey,
      profile: project.channel.profile,
      extra: { schedule: project.channel.schedule, featured: project.channel.featured, goal: project.channel.goal, raids: project.channel.raids, clips: project.channel.clips },
    });
  }

  async raidChannel(targetSlug: string): Promise<{ target: string; viewers: number }> {
    const project = this.current();
    const streamKey = project.output.streaming.swarmStreamKey.trim();
    if (project.output.streaming.mode !== 'swarm' || this.runtime.getState().swarm.status !== 'live') {
      throw new Error('Raids need a live stream on ompeer.com.');
    }
    const result = await raidChannel({ apiUrl: project.output.streaming.swarmApiUrl, streamKey, target: targetSlug });
    // The control plane ended the stream for everyone; this studio follows.
    await this.stopBroadcast();
    return { target: result.target, viewers: result.viewers };
  }

  async stopBroadcast(): Promise<void> {
    this.closeOrdinary();
    const { broadcast, whip, swarm } = this.runtime.getState();
    await Promise.all([
      broadcast.status !== 'idle' ? this.host.stop() : undefined,
      whip.status !== 'idle' ? this.whip.stop() : undefined,
      swarm.status !== 'idle' ? this.swarm.stop() : undefined,
      this.stopSavedBroadcast(),
    ]);
    if (this.liveRoomKey) clearSavedRoom(this.liveRoomKey);
    this.liveRoomKey = null;
    this.latestPeers = {};
    this.latestWhip = null;
    this.runtime.setState({ network: {} });
  }

  kickPeer(peerId: string): void {
    this.host.kick(peerId);
  }

  private broadcastRunning(): boolean {
    return this.runtime.getState().broadcast.status !== 'idle';
  }

  admitGuest(peerId: string): void {
    this.host.admitGuest(peerId);
  }

  rejectGuest(peerId: string, reason?: string): void {
    this.host.rejectGuest(peerId, reason);
  }

  revokeInvite(participantId: string): void {
    this.host.revokeInvite(participantId);
  }

  createInvite(options: CreateInviteOptions): Promise<InviteInfo> {
    return this.host.createInvite(options);
  }

  /** The choice is kept with the project so the next broadcast starts the same way. */
  async setGuestLinkEnabled(enabled: boolean): Promise<string> {
    this.commands.setStreamingSettings({ guestLink: enabled });
    return this.broadcastRunning() ? this.host.setGuestLinkEnabled(enabled) : '';
  }

  rotateGuestLink(): Promise<string> {
    return this.host.rotateGuestLink();
  }

  async setAllowGuests(allow: boolean): Promise<void> {
    this.commands.setStreamingSettings({ allowGuests: allow });
    if (this.broadcastRunning()) await this.host.setAllowGuests(allow);
    // Live on the swarm without a guest room yet: accepting guests opens it.
    else if (allow) await this.openSwarmGuestRoom();
  }

  // -------------------------------------------------------------------------
  // Health watchdog
  // -------------------------------------------------------------------------

  /**
   * The watchdog's clock (a Worker timer) is the only thing that samples
   * network stats: it keeps running when the tab is hidden, and a single
   * sampler keeps the stats deltas consistent.
   */
  private startHealthMonitor(): void {
    this.health = createHealthMonitor({
      logger: this.logger,
      getRenderStats: () => this.compositor.getStats(),
      getLastFrameTime: () => this.compositor.getLastFrameTime(),
      getProgramVideoTrack: () => this.programStream?.getVideoTracks()[0] ?? null,
      getProgramAudioTrack: () => this.programStream?.getAudioTracks()[0] ?? null,
      getAudioContext: () => this.audio.context,
      getAudioQuantaCount: () => this.audio.getQuantaCount(),
      getMasterPeakDb: () => Math.max(-Infinity, ...(this.audio.getLevels()[MASTER_STRIP]?.peakDb ?? [])),
      getPeerStats: async () => {
        const status = this.runtime.getState().broadcast.status;
        const peers = status === 'live' || status === 'reconnecting' ? await this.host.getStats() : {};
        this.publishNetwork({ peers });
        return peers;
      },
      getWhipStats: async () => {
        const status = this.runtime.getState().whip.status;
        const whip = status === 'live' || status === 'reconnecting' ? await this.whip.getStats() : null;
        this.publishNetwork({ whip });
        return whip;
      },
      getEncoderStats: () => {
        const stats: SwarmStats | null = this.runtime.getState().swarm.stats;
        return stats ? { queue: stats.encoderQueue, droppedFrames: stats.droppedFrames } : null;
      },
      getRecorderBytes: () => {
        const recording = this.recorder.getState();
        const active = recording.status === 'recording' || recording.status === 'paused';
        return { active, bytesWritten: recording.bytesWritten };
      },
      isLive: () => this.isLive(),
      recover: {
        resumeAudio: async () => {
          await this.audio.resume();
          return this.audio.getState() === 'running';
        },
        restartRenderLoop: () => this.compositor.recover(),
        reconnectPeer: async (peerId) => {
          await this.host.reconnectPeer(peerId);
          return true;
        },
      },
    });
    this.disposers.push(
      this.health.subscribe((health) => {
        this.runtime.setState({ health });
        this.announceIssues(health.issues);
        this.checkSourceFrames();
        this.refreshPolledSourceInfo();
      }),
      this.health.onEvent((event) => this.onHealthEvent(event)),
    );
    this.health.start();
  }

  private readonly frozenSources = new Set<Id>();

  /**
   * A camera, guest or playing clip can stop delivering pictures while its
   * status still reads live, so the compositor keeps sending its last frame at
   * full rate. Compare the new-picture counter of each on-air source that is
   * expected to deliver continuously (expectsContinuousFrames: a static screen
   * share or a paused clip legitimately idles) against the clock, and tell the
   * operator when one has gone still.
   */
  private checkSourceFrames(): void {
    const project = this.current();
    const { programSceneId } = this.runtime.getState();
    const onAir = programSceneId && project.scenes[programSceneId]
      ? queries.sourcesInScene(project, programSceneId, { visibleOnly: true })
      : new Set<Id>();
    const now = performance.now();
    for (const id of [...this.frozenSources]) {
      if (!onAir.has(id)) {
        this.frozenSources.delete(id);
        this.alert.clear(`source-frozen:${id}`);
      }
    }
    for (const id of onAir) {
      const source = project.sources[id];
      const runtime = this.sources.get(id);
      if (!source || !runtime || !hasFrameStats(runtime)) continue;
      let watched = false;
      let lastFrameAt = 0;
      try {
        watched = expectsContinuousFrames(runtime, source.kind) && runtime.getStatus().state === 'live';
        lastFrameAt = runtime.getFrameStats().lastFrameAt;
      } catch (error) {
        this.logger.log('warn', 'health', `Frame check of "${source.name}" failed`, error);
        continue;
      }
      const frozen = watched && lastFrameAt > 0 && now - lastFrameAt > SOURCE_FROZEN_MS;
      if (frozen && !this.frozenSources.has(id)) {
        this.frozenSources.add(id);
        const message = `"${source.name}" has not delivered a new picture for ${Math.round((now - lastFrameAt) / 1000)} s. Its last frame is still on air.`;
        this.logger.log('warn', 'health', message);
        this.notify('warn', message);
        if (this.isLive()) this.alert.raise(`source-frozen:${id}`, message);
      } else if (!frozen && this.frozenSources.has(id)) {
        this.frozenSources.delete(id);
        this.alert.clear(`source-frozen:${id}`);
        // A source that stopped on purpose (paused, ended) is not "delivering again".
        if (watched) this.notify('info', `"${source.name}" is delivering pictures again.`);
      }
    }
  }

  private latestPeers: Record<string, PeerStats> = {};
  private latestWhip: PeerStats | null = null;

  private publishNetwork(update: { peers?: Record<string, PeerStats>; whip?: PeerStats | null }): void {
    if (update.peers) this.latestPeers = update.peers;
    if (update.whip !== undefined) this.latestWhip = update.whip;
    const network: Record<string, PeerStats> = { ...this.latestPeers };
    if (this.latestWhip) network.whip = this.latestWhip;
    if (Object.keys(network).length > 0 || Object.keys(this.runtime.getState().network).length > 0) {
      this.runtime.setState({ network });
    }
  }

  /** An output that fails on its own must tell the operator, not just change a status field. */
  private reportOutputError(key: string, label: string, before: string | null, after: string | null): void {
    if (after && after !== before) {
      const message = `${label} error: ${after}`;
      this.notify('error', message);
      this.alert.raise(`output:${key}`, message);
    } else if (!after && before) {
      this.alert.clear(`output:${key}`);
    }
  }

  /** Ask for permission to show system notifications when the stream has a problem. */
  requestAlertPermission(): Promise<NotificationPermission | 'unsupported'> {
    return this.alert.requestPermission();
  }

  private readonly announcedIssues = new Set<string>();

  /**
   * Tell the operator about each new issue once, from the issue list itself so
   * the severity is the one the watchdog settled on: a notification in the app,
   * and for critical issues an alert that is noticeable outside the tab.
   */
  private announceIssues(issues: readonly HealthIssue[]): void {
    for (const issue of issues) {
      if (issue.code === 'page-hidden' || this.announcedIssues.has(issue.id)) continue;
      this.announcedIssues.add(issue.id);
      const critical = issue.severity === 'critical';
      this.notify(critical ? 'error' : 'warn', issue.message, issue.id);
      if (critical) this.alert.raise(issue.id, issue.message);
    }
  }

  /** Every stream problem the watchdog finds reaches the operator; none stays in a log only. */
  private onHealthEvent(event: HealthEvent): void {
    const level = event.type === 'recovery-failed' ? 'error' : 'warn';
    this.logger.log(event.type === 'issue-cleared' || event.type === 'recovery-succeeded' ? 'info' : level, 'health', `${event.type} ${event.code}: ${event.detail}`);
    if (event.code === 'page-hidden') return;
    const key = event.peerId ? `${event.code}:${event.peerId}` : event.code;
    if (event.type === 'issue-cleared') {
      this.announcedIssues.delete(key);
      this.clearCondition(key, event.detail);
    } else if (event.type === 'recovery-failed') {
      this.alert.raise(key, event.detail);
      this.notify('error', event.detail, key);
    } else if (event.type === 'recovery-succeeded') {
      this.notify('info', event.detail);
    }
  }

  async exportDiagnostics(): Promise<Blob> {
    const state = this.runtime.getState();
    const project = this.current();
    const report = {
      generatedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      visibility: document.visibilityState,
      project: {
        video: project.video,
        sampleRate: project.audio.sampleRate,
        scenes: project.sceneOrder.length,
        sources: Object.values(project.sources).map((source) => source.kind),
        streaming: {
          mode: project.output.streaming.mode,
          codec: project.output.streaming.codec,
          videoKbps: project.output.streaming.videoKbps,
          audioKbps: project.output.streaming.audioKbps,
          degradation: project.output.streaming.degradation,
          iceTransportPolicy: project.output.streaming.iceTransportPolicy,
          iceServerCount: project.output.streaming.iceServers.length,
        },
      },
      // Room links and keys are credentials; only the shape of the session is exported.
      // Guest names are personal data; invites and the lobby carry names and tokens.
      broadcast: {
        status: state.broadcast.status,
        error: state.broadcast.error,
        reconnectAttempts: state.broadcast.reconnectAttempts,
        peers: state.broadcast.peers.map((peer) => ({ role: peer.role, connectionState: peer.connectionState })),
      },
      whip: { status: state.whip.status, error: state.whip.error, reconnectAttempts: state.whip.reconnectAttempts },
      // The stream key is a credential; only the session's shape and counters are exported.
      swarm: {
        status: state.swarm.status,
        error: state.swarm.error,
        reconnectAttempts: state.swarm.reconnectAttempts,
        streamId: state.swarm.streamId,
        stats: state.swarm.stats,
      },
      recording: state.recording,
      render: state.render,
      network: state.network,
      sources: state.sources,
      audioContextState: state.audioContextState,
      health: this.health.getState(),
      healthHistory: this.health.getHistory(),
      logs: this.logger.entries(),
    };
    return new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  }

  listRecordingFormats(): RecordingFormat[] {
    return this.recorder.listSupportedFormats();
  }

  async startRecording(): Promise<void> {
    await this.resumeAudio();
    const project = this.current();
    const settings = project.output.recording;
    const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ').replace(/:/g, '-');
    const base = project.name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'Recording';
    await this.recorder.start({
      stream: this.requireProgramStream(),
      mimeType: settings.mimeType,
      videoKbps: settings.videoKbps,
      audioKbps: settings.audioKbps,
      sink: settings.sink,
      fileName: `${base} ${stamp}`,
    });
  }

  pauseRecording(): void {
    this.recorder.pause();
  }

  resumeRecording(): void {
    this.recorder.resume();
  }

  stopRecording(): Promise<RecordingResult> {
    return this.recorder.stop();
  }

  listRecordings(): Promise<RecordingResult[]> {
    return this.recorder.listRecordings();
  }

  getRecordingBlob(id: string): Promise<Blob | null> {
    return this.recorder.getRecordingBlob(id);
  }

  downloadRecording(id: string): Promise<void> {
    return this.recorder.download(id);
  }

  deleteRecording(id: string): Promise<void> {
    return this.recorder.deleteRecording(id);
  }

  async saveClip(): Promise<RecordingResult> {
    const project = this.current();
    if (!project.channel.clips.enabled) throw new Error('Clips are turned off for this channel.');
    if (this.replay.getState().status !== 'running') throw new Error('Clips can be made while streaming or recording.');
    const stamp = new Date().toISOString().slice(0, 19).replace('T', ' ').replace(/:/g, '-');
    const base = project.name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'Recording';
    const fileName = `${base} clip ${stamp}`;
    const clip = await this.replay.save(fileName);
    return this.recorder.importRecording(clip.blob, { fileName, sink: project.output.recording.sink, durationMs: clip.durationMs });
  }

  /**
   * Runs the clips replay buffer while anything is streamed or recorded and clips are on, and
   * restarts it when its stream or settings change. A buffer that failed is retried only then.
   */
  private syncReplayBuffer(): void {
    if (this.syncingReplay || this.destroyed) return;
    this.syncingReplay = true;
    try {
      const project = this.current();
      const { clips } = project.channel;
      const stream = this.programStream;
      if (!clips.enabled || !stream || !this.isLive()) {
        this.replayOptions = null;
        if (this.replay.getState().status !== 'idle') this.replay.stop();
        return;
      }
      const settings = project.output.recording;
      const wanted: ReplayBufferStartOptions = {
        stream,
        mimeType: settings.mimeType,
        videoKbps: settings.videoKbps,
        audioKbps: settings.audioKbps,
        seconds: clips.seconds,
      };
      if (this.replayOptions && shallowEqual(this.replayOptions, wanted) && this.replay.getState().status !== 'idle') return;
      this.replayOptions = wanted;
      try {
        this.replay.start(wanted);
      } catch (err) {
        this.logger.log('warn', 'studio', `Replay buffer did not start: ${errorMessage(err)}`);
      }
    } finally {
      this.syncingReplay = false;
    }
  }

  /** Deletes finished recordings older than `channel.vodRetentionDays` (0 keeps everything). */
  private purgeOldRecordings(): Promise<void> {
    const run = this.purgeChain.then(async () => {
      const days = this.current().channel.vodRetentionDays;
      if (!(days > 0) || this.destroyed) return;
      // listRecordings() only lists finished recordings, so one in progress is never touched.
      const doomed = recordingsToPurge(await this.recorder.listRecordings(), days, Date.now());
      let removed = 0;
      for (const rec of doomed) {
        try {
          await this.recorder.deleteRecording(rec.id);
          removed++;
        } catch (err) {
          this.logger.log('warn', 'studio', `Could not delete expired recording ${rec.fileName}`, err);
        }
      }
      this.logger.log('info', 'studio', `Retention (${days} days): removed ${removed} recording(s)`);
    });
    this.purgeChain = run.catch((err: unknown) => this.logger.log('warn', 'studio', `Retention purge failed: ${errorMessage(err)}`));
    return this.purgeChain;
  }

  // -------------------------------------------------------------------------
  // Assets and collections
  // -------------------------------------------------------------------------

  importAsset(file: Blob, name: string, options?: AssetPutOptions): Promise<AssetMeta> {
    return this.assets.put(file, name, options);
  }

  getAssetUrl(assetId: string): Promise<string | null> {
    return this.assets.getUrl(assetId);
  }

  getAssetMeta(assetId: string): Promise<AssetMeta | null> {
    return this.assets.getMeta(assetId);
  }

  private async loadInitialProject(): Promise<Project> {
    try {
      const lastId = await this.repository.getLastOpenedId();
      const candidates = lastId ? [lastId] : (await this.repository.list()).map((summary) => summary.id);
      for (const id of candidates) {
        const project = await this.repository.load(id);
        if (project) return project;
      }
    } catch (error) {
      this.logger.log('error', 'studio', 'Could not restore the last scene collection', error);
      this.notify('error', `Could not restore your last scene collection: ${errorMessage(error)}`);
    }
    const project = createDefaultProject();
    await this.repository.save(project).catch((error: unknown) => {
      this.notify('error', `Local storage is unavailable; changes will not be saved (${errorMessage(error)}).`);
    });
    return project;
  }

  private scheduleSave(): void {
    const collection = this.runtime.getState().collection;
    if (!collection.dirty) this.runtime.setState({ collection: { ...collection, dirty: true } });
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.flushSave(), AUTOSAVE_DELAY_MS);
  }

  private flushSave(): Promise<void> {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.saveChain = this.saveChain.then(async () => {
      if (!this.runtime.getState().collection.dirty) return;
      const project = this.current();
      try {
        await this.repository.save(project);
      } catch (error) {
        this.notify('error', `Saving failed: ${errorMessage(error)}`);
        return;
      }
      const collection = this.runtime.getState().collection;
      if (collection.id !== project.id) return;
      this.runtime.setState({
        collection: { ...collection, dirty: this.current() !== project, lastSavedAt: Date.now() },
      });
    });
    return this.saveChain;
  }

  save(): Promise<void> {
    return this.flushSave();
  }

  listCollections(): Promise<ProjectSummary[]> {
    return this.repository.list();
  }

  private async switchTo(project: Project): Promise<void> {
    await this.flushSave();
    this.openProject(project);
    await this.syncPlugins(project);
  }

  async newCollection(name: string): Promise<void> {
    const project = createDefaultProject(name);
    await this.repository.save(project);
    await this.switchTo(project);
  }

  async openCollection(id: Id): Promise<void> {
    if (id === this.current().id) return;
    await this.flushSave();
    const project = await this.repository.load(id);
    if (!project) throw new Error('That scene collection no longer exists.');
    await this.switchTo(project);
  }

  async duplicateCollection(name: string): Promise<void> {
    await this.flushSave();
    const now = Date.now();
    const copy: Project = { ...this.current(), id: createId(), name, createdAt: now, updatedAt: now };
    await this.repository.save(copy);
    await this.switchTo(copy);
  }

  async deleteCollection(id: Id): Promise<void> {
    if (id !== this.current().id) {
      await this.repository.delete(id);
      return;
    }
    await this.flushSave();
    await this.repository.delete(id);
    clearSavedRoom(id);
    const remaining = await this.repository.list();
    let next: Project | null = null;
    for (const summary of remaining) {
      next = await this.repository.load(summary.id);
      if (next) break;
    }
    if (!next) {
      next = createDefaultProject();
      await this.repository.save(next);
    }
    // The deleted document must not be written back by a pending autosave.
    const collection = this.runtime.getState().collection;
    this.runtime.setState({ collection: { ...collection, dirty: false } });
    this.openProject(next);
    await this.syncPlugins(next);
  }

  exportCollection(options: { includeAssets: boolean }): Promise<Blob> {
    return this.bundler.exportBundle(this.current(), options);
  }

  async importCollection(file: Blob): Promise<void> {
    const imported = await this.bundler.importBundle(file);
    // A collection file can name plugin URLs; never run code just because a file was imported.
    const hadPlugins = imported.plugins.some((plugin) => plugin.enabled);
    // The same goes for where the program is sent and which keys trigger it:
    // destinations, relay servers and hotkeys come from this machine, not the file.
    const local = this.current().output.streaming;
    const project: Project = {
      ...imported,
      plugins: imported.plugins.map((plugin) => ({ ...plugin, enabled: false })),
      hotkeys: {},
      output: {
        ...imported.output,
        streaming: {
          ...imported.output.streaming,
          signalingUrl: local.signalingUrl,
          iceServers: local.iceServers,
          whipEndpoint: '',
          whipBearerToken: '',
          // Where the stream key is sent: a crafted file must not choose it (it would receive the key
          // as a Bearer token on the next go-live). Reset like the key, never taken from the file.
          swarmApiUrl: DEFAULT_SWARM_API_URL,
          swarmStreamKey: '',
        },
      },
    };
    await this.repository.save(project);
    await this.switchTo(project);
    if (hadPlugins) {
      this.notify('warn', 'This collection references plugins. They were left disabled; load the ones you trust again from Settings → Plugins.');
    }
  }

  // -------------------------------------------------------------------------
  // Plugins and contributions
  // -------------------------------------------------------------------------

  private async syncPlugins(project: Project): Promise<void> {
    const wanted = new Map(project.plugins.filter((plugin) => plugin.enabled).map((plugin) => [plugin.id, plugin]));
    for (const loaded of this.plugins.list()) {
      if (wanted.get(loaded.manifest.id)?.url === loaded.url) continue;
      await this.plugins.unload(loaded.manifest.id).catch((error: unknown) => {
        this.logger.log('warn', 'plugins', `Unloading ${loaded.manifest.id} failed`, error);
      });
    }
    const loadedIds = new Set(this.plugins.list().map((plugin) => plugin.manifest.id));
    for (const plugin of wanted.values()) {
      if (loadedIds.has(plugin.id)) continue;
      try {
        await this.plugins.load(plugin.url);
      } catch (error) {
        this.notify('error', `Plugin "${plugin.id}" failed to load: ${errorMessage(error)}`);
      }
    }
    this.refreshPluginList();
    this.events.emit('plugins-synchronized', { id: this.current().id });
  }

  private refreshPluginList(): void {
    const loaded = this.plugins.list();
    const plugins: LoadedPluginInfo[] = loaded.map((plugin) => ({
      id: plugin.manifest.id,
      name: plugin.manifest.name,
      version: plugin.manifest.version,
      url: plugin.url,
      status: plugin.status,
      error: plugin.error,
    }));
    const known = new Set(plugins.map((plugin) => plugin.id));
    for (const ref of this.current().plugins) {
      if (known.has(ref.id)) continue;
      plugins.push({
        id: ref.id,
        name: ref.id,
        version: '',
        url: ref.url,
        status: ref.enabled ? 'error' : 'disabled',
        error: ref.enabled ? 'Not loaded' : null,
      });
    }
    this.runtime.setState({ plugins });
  }

  async loadPlugin(url: string): Promise<void> {
    const manifest = await this.plugins.load(url);
    const others = this.current().plugins.filter((plugin) => plugin.id !== manifest.id);
    this.commands.setPlugins([...others, { id: manifest.id, url, enabled: true }]);
    this.refreshPluginList();
  }

  async unloadPlugin(id: string): Promise<void> {
    if (this.plugins.list().some((plugin) => plugin.manifest.id === id)) await this.plugins.unload(id);
    this.commands.setPlugins(this.current().plugins.filter((plugin) => plugin.id !== id));
    this.refreshPluginList();
  }

  registerDock(dock: DockContribution): Unsubscribe {
    this.runtime.setState((state) => ({ docks: [...state.docks.filter((d) => d.id !== dock.id), dock] }));
    return () => this.runtime.setState((state) => ({ docks: state.docks.filter((d) => d !== dock) }));
  }

  registerHotkeyAction(action: HotkeyAction): Unsubscribe {
    this.runtime.setState((state) => ({
      hotkeyActions: [...state.hotkeyActions.filter((a) => a.id !== action.id), action],
    }));
    return () => this.runtime.setState((state) => ({ hotkeyActions: state.hotkeyActions.filter((a) => a !== action) }));
  }

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------

  /**
   * Automatic captions run while this studio is live on the swarm (reconnects included) with
   * captions on, and stop when it goes offline or captions are turned off.
   */
  private syncCaptions(): void {
    if (this.destroyed) return;
    const target = captionTargetFor(this.current().output.streaming, this.runtime.getState().swarm);
    if (target) this.captions.start(target);
    else this.captions.stop();
  }

  reportPrompterLink(link: PrompterLink): void {
    if (this.runtime.getState().prompterLink !== link) this.runtime.setState({ prompterLink: link });
  }

  reportNativeOperational(native: boolean): void {
    if (this.runtime.getState().nativeOperational !== native) this.runtime.setState({ nativeOperational: native });
  }

  notify(level: Notification['level'], message: string, conditionKey?: string): void {
    const notification: Notification = { id: createId(), level, message, at: Date.now(), ...(conditionKey ? { conditionKey } : {}) };
    this.runtime.setState((state) => ({
      notifications: [...state.notifications, notification].slice(-MAX_NOTIFICATIONS),
    }));
  }

  /**
   * A condition that has ended must not vanish without trace: an operator who
   * was away should still learn that it happened. Its notifications stay, but
   * are downgraded and say how it was resolved: the original text often asks
   * for an action that is wrong once the problem is gone.
   */
  private clearCondition(conditionKey: string, resolvedText: string): void {
    this.alert.clear(conditionKey);
    this.runtime.setState((state) => {
      if (!state.notifications.some((n) => n.conditionKey === conditionKey && n.level === 'error')) return state;
      return {
        notifications: state.notifications.map((n) =>
          n.conditionKey === conditionKey && n.level === 'error'
            ? { ...n, level: 'warn' as const, message: resolvedText || `Resolved: ${n.message}`, at: Date.now() }
            : n,
        ),
      };
    });
  }

  dismissNotification(id: string): void {
    this.runtime.setState((state) => ({ notifications: state.notifications.filter((n) => n.id !== id) }));
  }
}

function sameSourceInfo(a: SourceRuntimeInfo, b: SourceRuntimeInfo): boolean {
  const { live: liveA, media: mediaA, ...restA } = a;
  const { live: liveB, media: mediaB, ...restB } = b;
  const same = <T extends object>(x: T | undefined, y: T | undefined) => (x && y ? shallowEqual(x, y) : x === y);
  return shallowEqual(restA, restB) && same(liveA, liveB) && same(mediaA, mediaB);
}

function readSavedRoom(projectId: Id, signalingUrl: string): SavedRoom | null {
  try {
    const raw = sessionStorage.getItem(ROOM_STORAGE_PREFIX + projectId);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<SavedRoom>;
    if (saved.signalingUrl !== signalingUrl || typeof saved.roomId !== 'string' || typeof saved.hostKey !== 'string') {
      return null;
    }
    return { signalingUrl, roomId: saved.roomId, hostKey: saved.hostKey };
  } catch {
    return null;
  }
}

function writeSavedRoom(projectId: Id, room: SavedRoom): void {
  try {
    sessionStorage.setItem(ROOM_STORAGE_PREFIX + projectId, JSON.stringify(room));
  } catch {
    // Storage may be blocked; the room simply will not be reclaimable after a reload.
  }
}

function clearSavedRoom(projectId: Id): void {
  try {
    sessionStorage.removeItem(ROOM_STORAGE_PREFIX + projectId);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

export interface StudioOptions {
  /** Fixed-operation portal RPC; account credentials never enter Studio. */
  gamingProvider?: GamingControlProvider;
  gamingTelemetry?:import('../gaming/telemetry').GamingTelemetrySink;
  /** Compiled local model, never executable data from the Portal RPC. */
  gamingSelectedComposition?:import('../gaming/remote/composition').SelectedStudioComposition;
  gamingCreatorQualification?:CreatorQualifier;
  gamingOrdinaryProgram?:OrdinaryProgramFactory;
  /**
   * Running inside the portal's creator dashboard (`#embed=1`): the portal guards closing the page while
   * live, so the studio registers no `beforeunload` of its own, and system notifications are not offered
   * (browsers refuse them to cross-origin frames).
   */
  embedded?: boolean;
}

export function createStudio(options: StudioOptions = {}): StudioApi {
  return new Studio(options);
}
